/**
 * Feature Flags: Root links acquisition and caching.
 */

import { axios } from '../../../internal';
import { getLogger } from '../../../logging';
import { Dict, LinkType, RootLinksType } from '../../types';
import { createLogMsgFormatter, FEATURE_FLAGS_LOGGER_COMPONENT } from './common';

const logger = getLogger(FEATURE_FLAGS_LOGGER_COMPONENT);
const L = createLogMsgFormatter('root links');

const SERVICE_ROOT = '/featureFlags/';
const SAS_API_MEDIA_TYPE = 'application/vnd.sas.api+json';

const HARDCODED_ROOT_LINKS: Dict<LinkType> = {
  enabledTrueBulk: {
    method: 'GET',
    rel: 'enabledTrueBulk',
    href: '/featureFlags/enabled?onlyEnabled=true',
    uri: '/featureFlags/enabled?onlyEnabled=true',
    type: 'application/vnd.sas.collection',
    itemType: 'application/vnd.sas.feature.flag',
  },
};

// *** Common REST Methods ***

let cachedRootLinkMapPromise: Promise<Dict<LinkType>> | null = null;

// Retrieves, maps and caches the Feature Flags Service root HATEOAS links.
/** @internal */
export function getRootLink(rel: string): Promise<LinkType | undefined> {
  // check for and return hardcoded link to subvert root links request
  const link = HARDCODED_ROOT_LINKS[rel];
  if (link) {
    return Promise.resolve(link);
  }

  if (!cachedRootLinkMapPromise) {
    logger.info(L('retrieving...'));

    cachedRootLinkMapPromise = axios
      .get<RootLinksType>(SERVICE_ROOT, {
        headers: {
          Accept: SAS_API_MEDIA_TYPE,
        },
      })
      .then((resp) => {
        logger.info(L('received'));

        // convert the array of root links to a link map, if possible
        const links = resp.data?.links;
        if (!Array.isArray(links)) {
          throw new Error("cannot find 'links' array in featureFlags root request");
        }

        return links.reduce((map: Dict<LinkType>, link: LinkType) => {
          if (link.rel) {
            map[link.rel] = link;
          }
          return map;
        }, {});
      })
      .catch((err) => {
        logger.warn(L(err));
        logger.warn(L('ignoring errors and assuming no root links'));

        // ignore service errors
        return {};
      });
  }
  return cachedRootLinkMapPromise.then((map) => map[rel]);
}

// Invalidates the cache for the root links.
/** @internal */
export function clearRootLinksCache() {
  logger.info(L('clearing cache'));
  cachedRootLinkMapPromise = null;
}
